// frontend/src/components/regulatory/RegulatoryAlertCard.tsx
import { useState } from "react";
import { ExternalLink, AlertTriangle, Check } from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { cn } from "../../lib/cn";
import { useSession } from "../../lib/auth";
import { markCandidateReviewed, unmarkCandidateReviewed } from "../../lib/dataFetchers";
import { Button } from "../ui/Button";
import type { RuleChangeCandidate, Country, RegwatchDomain, RegwatchConfidence, EntityRegistrySnapshot } from "../../lib/types";

// ─── Domain chip colours ──────────────────────────────────────────────────────

const DOMAIN_CHIP_CLASS: Record<RegwatchDomain, string> = {
  renewal_obligation:     "bg-[#eff6ff] text-[#1d4ed8] border-[#bfdbfe]",
  ownership_ubo:          "bg-[#fdf4ff] text-[#7c3aed] border-[#e9d5ff]",
  share_capital:          "bg-[#fff7ed] text-[#c2410c] border-[#fed7aa]",
  directors_officers:     "bg-[#f0fdf4] text-[#15803d] border-[#bbf7d0]",
  licensing_permits:      "bg-[#fefce8] text-[#a16207] border-[#fef08a]",
  tax_vat_registration:   "bg-[#f0f9ff] text-[#0369a1] border-[#bae6fd]",
  corporate_form:         "bg-[#fdf2f8] text-[#be185d] border-[#fbcfe8]",
  employment_eor:         "bg-[#fff1f2] text-[#be123c] border-[#fecdd3]",
  registered_office_lease:"bg-[#f7fee7] text-[#3f6212] border-[#d9f99d]",
  reporting_substance:    "bg-[#fafaf9] text-[#57534e] border-[#d6d3d1]",
  other:                  "bg-[#f8fafc] text-[#475569] border-[#cbd5e1]",
  entity_registry:        "bg-[#f0f4ff] text-[#3730a3] border-[#c7d2fe]",
  personal_income_tax_rate:        "bg-[#ecfeff] text-[#0e7490] border-[#a5f3fc]",
  social_security_contribution_rate: "bg-[#f5f3ff] text-[#6d28d9] border-[#ddd6fe]",
};

// ─── Confidence badge colours ─────────────────────────────────────────────────

const CONFIDENCE_CLASS: Record<RegwatchConfidence, string> = {
  low:  "bg-[#f1f5f9] text-[#64748b]",
  med:  "bg-[#fffbeb] text-[#92400e]",
  high: "bg-[#f0fdf4] text-[#166534]",
};

// ─── Entity Registry Snapshot renderer ───────────────────────────────────────

interface RegistrySnapshotProps {
  snapshot: EntityRegistrySnapshot;
}

function RegistrySnapshot({ snapshot }: RegistrySnapshotProps): JSX.Element {
  const { t } = useTranslation("regulatory");

  // Introduce locals to avoid non-null assertions on optional arrays (fix #4)
  const dueDates = snapshot.dueDates ?? [];
  const officers = snapshot.officers ?? [];

  const hasDueDates = dueDates.length > 0;
  const hasOfficers = officers.length > 0;

  return (
    <div
      data-testid="registry-snapshot"
      className="rounded-[8px] bg-surface-2 border border-border-2 px-3 py-2.5 space-y-3 text-[12.5px]"
    >
      {/* Status */}
      {snapshot.companyStatus && (
        <div className="flex items-baseline gap-2">
          <span className="font-semibold text-dim uppercase tracking-[.04em] text-[10px] shrink-0">
            {t("registry_snapshot_status")}
          </span>
          <span className="text-text font-medium">{snapshot.companyStatus}</span>
        </div>
      )}

      {/* Registered office */}
      {snapshot.registeredOffice && (
        <div className="flex items-baseline gap-2">
          <span className="font-semibold text-dim uppercase tracking-[.04em] text-[10px] shrink-0">
            {t("registry_snapshot_office")}
          </span>
          <span className="text-text">{snapshot.registeredOffice}</span>
        </div>
      )}

      {/* Due dates (filing obligations) */}
      {hasDueDates && (
        <div>
          <p className="font-semibold text-dim uppercase tracking-[.04em] text-[10px] mb-1.5">
            {t("registry_snapshot_due_dates")}
          </p>
          <ul className="space-y-1">
            {dueDates.map((dd) => (
              <li
                key={`${dd.description}-${dd.dueDate ?? ""}`}
                className={cn(
                  "flex items-center gap-2 text-[12px]",
                  dd.overdue && "text-sev-expired",
                )}
              >
                {dd.overdue && (
                  // Icon is decorative — the visible badge below already announces "Overdue" (fix #3)
                  <AlertTriangle className="h-3 w-3 shrink-0 text-sev-expired" aria-hidden />
                )}
                <span className={cn("font-medium", !dd.overdue && "text-text")}>
                  {dd.description}
                </span>
                {dd.dueDate && (
                  <span className="text-dim">— {dd.dueDate}</span>
                )}
                {dd.overdue && (
                  <span className="text-[10px] font-semibold uppercase tracking-wide bg-sev-expired/10 text-sev-expired rounded px-1.5 py-0.5">
                    {t("registry_snapshot_overdue")}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Officers */}
      {hasOfficers && (
        <div>
          <p className="font-semibold text-dim uppercase tracking-[.04em] text-[10px] mb-1.5">
            {t("registry_snapshot_officers")} ({officers.length})
          </p>
          <ul className="space-y-0.5">
            {officers.map((officer) => (
              <li key={`${officer.name}-${officer.role ?? ""}`} className="flex items-baseline gap-1.5 text-[12px]">
                <span className="font-medium text-text">{officer.name}</span>
                <span className="text-dim">— {officer.role}</span>
                {officer.appointedDate && (
                  <span className="text-dim text-[11px]">({officer.appointedDate})</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Other notable items */}
      {snapshot.otherNotableItems && (
        <div>
          <p className="font-semibold text-dim uppercase tracking-[.04em] text-[10px] mb-1">
            {t("registry_snapshot_other")}
          </p>
          <p className="text-text leading-relaxed">{snapshot.otherNotableItems}</p>
        </div>
      )}

      {/* Effective date */}
      {snapshot.effectiveDate && (
        <p className="text-dim text-[11px] border-t border-border-2 pt-2">
          {t("registry_snapshot_effective")}: {snapshot.effectiveDate}
        </p>
      )}
    </div>
  );
}

// ─── Props ────────────────────────────────────────────────────────────────────

interface RegulatoryAlertCardProps {
  candidate: RuleChangeCandidate;
  /** Pass the Country row (contains lawyerTaxCorporate / accountant). */
  country: Country | null;
  /** Entities keyed by id, for resolving names from affectedEntityIds. */
  entityNames?: Record<string, string>;
}

// ─── Component ───────────────────────────────────────────────────────────────

export function RegulatoryAlertCard({
  candidate,
  country,
  entityNames = {},
}: RegulatoryAlertCardProps): JSX.Element {
  const { t, i18n } = useTranslation("regulatory");
  const { user } = useSession();
  const qc = useQueryClient();
  const isEntityRegistry = candidate.domain === "entity_registry";
  const isLowConfidence = candidate.confidence === "low";

  // Optimistic UI — flip to "Reviewed" instantly on click; revert on server
  // error. The pill stays green either via this local flag or via the next
  // query refetch promoting `candidate.reviewed` to true.
  const [optimisticReviewed, setOptimisticReviewed] = useState(false);
  const effectiveReviewed = candidate.reviewed || optimisticReviewed;

  // Per-person review mutation. The button is hidden when there is no signed-in
  // user id — the backend 401s without a uid (legacy tokens). onSuccess
  // invalidates the feed AND the sidebar count so both refresh.
  // Drive POST vs DELETE off the explicit intent (`next`), NOT off the server
  // prop `candidate.reviewed`. The prop lags behind the optimistic flip until
  // the onSuccess refetch lands; keying the request off it let a quick
  // mark→undo within that window re-POST instead of DELETE (double-review).
  const reviewMutation = useMutation({
    mutationFn: (next: boolean) =>
      next ? markCandidateReviewed(candidate.id) : unmarkCandidateReviewed(candidate.id),
    onSuccess: (_data, next) => {
      if (next) toast.success(t("mark_reviewed_success"));
      void qc.invalidateQueries({ queryKey: ["regulatory-candidates"] });
      void qc.invalidateQueries({ queryKey: ["regulatory-count"] });
    },
    onError: () => {
      // Revert the optimistic flip; effectiveReviewed falls back to server truth.
      setOptimisticReviewed(false);
      toast.error(t("mark_reviewed_error"));
    },
  });

  const canReview = Boolean(user?.id);

  const domainShortKey = `domain_short_${candidate.domain}` as const;
  const domainLongKey  = `domain_${candidate.domain}` as const;
  const confidenceKey  = `confidence_${candidate.confidence}` as const;

  return (
    <article
      data-testid="regulatory-alert-card"
      data-candidate-id={candidate.id}
      data-apply-mode={candidate.applyMode}
      data-domain={candidate.domain}
      className="bg-surface border border-border rounded-[11px] shadow-card p-[15px_16px] flex flex-col gap-3"
    >
      {/* ── Row 0: country (flag + name) — group header is by detected date now ── */}
      {country && (
        <div
          data-testid="card-country"
          className="flex items-center gap-1.5 text-[12.5px] font-medium text-muted"
        >
          <span className="text-base leading-none" aria-hidden>
            {country.flag}
          </span>
          {/* country.name is a proper noun / legal place name — rendered verbatim */}
          <span lang="zxx">{country.name}</span>
        </div>
      )}

      {/* ── Row 1: domain chip + confidence + "Needs review" badge — and the
          top-right review action (Slack/Linear/Gmail pattern: per-person
          "Mark reviewed" lives next to the alert meta, not buried in the
          footer). Meta wraps on small screens; review action stays pinned
          to the right via `shrink-0`. ── */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2 min-w-0">
          <span
            title={t(domainLongKey)}
            data-testid="domain-chip"
            className={cn(
              "inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium border",
              DOMAIN_CHIP_CLASS[candidate.domain] ?? DOMAIN_CHIP_CLASS["other"],
            )}
          >
            {t(domainShortKey)}
          </span>

          <span
            className={cn(
              "inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium",
              CONFIDENCE_CLASS[candidate.confidence],
            )}
          >
            {t("confidence_label")}: {t(confidenceKey)}
          </span>

          {/* "Needs review" badge — shown for entity_registry low-confidence candidates */}
          {isEntityRegistry && isLowConfidence && (
            <span
              data-testid="needs-review-badge"
              className={cn(
                "inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-medium",
                "bg-amber-50 text-amber-700 border border-amber-200",
              )}
            >
              <AlertTriangle className="h-3 w-3" aria-hidden />
              {t("needs_review_badge")}
            </span>
          )}
        </div>

        {/* Top-right review action — only when the user can review.
            Reviewed: non-clickable green success Button + tiny "undo" link
            below (Gmail pattern). Unreviewed: clickable secondary Button
            with optimistic flip to the reviewed state on click. */}
        {canReview && (
          <div
            data-testid="review-footer"
            className="flex flex-col items-end gap-1 shrink-0"
          >
            {effectiveReviewed ? (
              <>
                <Button
                  variant="success"
                  data-testid="reviewed-pill"
                  disabled
                  aria-live="polite"
                >
                  <Check className="h-3.5 w-3.5 shrink-0" aria-hidden />
                  {t("status_reviewed")}
                </Button>
                <button
                  type="button"
                  data-testid="mark-unreviewed-button"
                  onClick={() => {
                    setOptimisticReviewed(false);
                    reviewMutation.mutate(false);
                  }}
                  disabled={reviewMutation.isPending}
                  className={cn(
                    "text-[11px] text-muted hover:text-text underline-offset-2 hover:underline",
                    "focus:outline-none focus:ring-2 focus:ring-accent/30 rounded",
                    "disabled:opacity-50",
                  )}
                >
                  {t("mark_unreviewed")}
                </button>
              </>
            ) : (
              <Button
                variant="secondary"
                data-testid="mark-reviewed-button"
                onClick={() => {
                  setOptimisticReviewed(true);
                  reviewMutation.mutate(true);
                }}
                disabled={reviewMutation.isPending}
              >
                <Check className="h-3.5 w-3.5 shrink-0" aria-hidden />
                {t("mark_reviewed")}
              </Button>
            )}
          </div>
        )}
      </div>

      {/* ── Row 2: advisory note for entity_registry ── */}
      {isEntityRegistry && (
        <p className="text-[12px] text-muted bg-surface-2 rounded-[7px] px-3 py-2 border border-border-2">
          {t("entity_registry_advisory_note")}
        </p>
      )}

      {/* ── Row 3: open official source ── */}
      <a
        href={candidate.sourceUrl}
        target="_blank"
        rel="noopener noreferrer"
        className={cn(
          "inline-flex items-center gap-1.5 text-sm font-medium text-accent",
          "hover:underline focus:outline-none focus:ring-2 focus:ring-accent/30 rounded",
          "min-h-[44px] sm:min-h-0",
        )}
        aria-label={`${t("open_source")} — ${candidate.sourceName ?? candidate.sourceUrl}`}
      >
        <ExternalLink className="h-4 w-4 shrink-0" aria-hidden />
        {t("open_source")}
        {candidate.sourceName && (
          <span className="text-text-muted font-normal">— {candidate.sourceName}</span>
        )}
      </a>

      {/* ── Row 4: dates ── */}
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-[12px] text-muted">
        <span>
          <span className="font-medium text-dim">{t("published_date")}:</span>{" "}
          {candidate.publishedDate}
        </span>
        <span>
          <span className="font-medium text-dim">{t("detected_date")}:</span>{" "}
          {new Date(candidate.detectedDate).toLocaleDateString(i18n.language)}
        </span>
        {candidate.effectiveDate && (
          <span data-testid="effective-date">
            <span className="font-medium text-dim">{t("effective_date")}:</span>{" "}
            {/* effectiveDate is a legal value from the source — rendered verbatim */}
            <span lang="zxx">{candidate.effectiveDate}</span>
          </span>
        )}
      </div>

      {/* ── Row 5: verbatim source quote ── */}
      <blockquote
        data-testid="source-quote"
        className="border-l-[3px] border-border-2 pl-3 text-[13px] text-muted italic leading-relaxed"
        lang="zxx"   /* untranslated verbatim content */
      >
        {/* sourceQuote is NEVER translated — rendered verbatim */}
        {candidate.sourceQuote}
      </blockquote>

      {/* ── Row 6: summary ── */}
      <p
        data-testid="candidate-summary"
        className="text-[13.5px] text-text leading-relaxed"
        lang="zxx"   /* untranslated verbatim content */
      >
        {/* summary is NEVER translated — rendered verbatim */}
        {candidate.summary}
      </p>

      {/* ── Row 7: entity registry structured snapshot ── */}
      {isEntityRegistry && candidate.entityRegistrySnapshot && (
        <RegistrySnapshot snapshot={candidate.entityRegistrySnapshot} />
      )}

      {/* ── Row 8: affected entities (with deep-link for entity_registry) ── */}
      {candidate.affectedEntityIds.length > 0 && (
        <div>
          <p className="text-[11px] uppercase tracking-[.05em] text-dim font-bold mb-1">
            {isEntityRegistry
              ? t("entity_registry_affected_entities")
              : t("affected_entities_label")}
          </p>
          <ul className="flex flex-wrap gap-2">
            {candidate.affectedEntityIds.map((eid) => (
              <li key={eid}>
                <Link
                  to={`/entities/${eid}`}
                  className="text-xs text-accent hover:underline focus:outline-none focus:ring-1 focus:ring-accent/30 rounded"
                  aria-label={`${t("entity_registry_view_entity")} — ${entityNames[eid] ?? eid}`}
                >
                  {entityNames[eid] ?? eid}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* ── Row 9: country service-provider block (not shown for entity_registry) ── */}
      {!isEntityRegistry && (country?.lawyerTaxCorporate ?? country?.accountant) && (
        <div className="rounded-[8px] bg-surface-2 border border-border-2 px-3 py-2 text-xs text-muted space-y-0.5">
          <p className="font-bold text-dim uppercase tracking-[.05em] text-[10px]">
            {t("service_provider_label")}
          </p>
          {country?.lawyerTaxCorporate && (
            <p>
              <span className="font-medium text-text">{t("service_provider_lawyer")}:</span>{" "}
              {country.lawyerTaxCorporate}
            </p>
          )}
          {country?.accountant && (
            <p>
              <span className="font-medium text-text">{t("service_provider_accountant")}:</span>{" "}
              {country.accountant}
            </p>
          )}
        </div>
      )}


    </article>
  );
}
